import React, { createContext, useMemo, ReactElement, useContext } from 'react';
import { Option } from 'fp-ts/lib/Option';
import { CriteriaCallback, useCase, UseCaseDispatch, UseCaseState } from 'adapter/in/common/hooks/useCase';
import { ReadUserGroup } from 'use-case/userGroups/read/readUserGroupsUseCase';
import { DashboardUserGroup } from 'domain/entity/dashboard';

export type UserGroupState = UseCaseState & { userGroups: Option<DashboardUserGroup[]> };

export const UserGroupsStateContext = createContext({} as UserGroupState);
export const useUserGroups = (): UserGroupState => useContext(UserGroupsStateContext);

type UserGroupsDispatch = UseCaseDispatch & { setUserGroups: (permissions: DashboardUserGroup[]) => void; read: CriteriaCallback };

const UserGroupsDispatchContext = createContext({} as UserGroupsDispatch);
export const dispatchUserGroups = (): UserGroupsDispatch => useContext(UserGroupsDispatchContext);

type UserGroupsProviderProps = {
    readUserGroups: ReadUserGroup;
    children: ReactElement;
};
export const UserGroupsProvider = (props: UserGroupsProviderProps): ReactElement => {
    const { children, readUserGroups } = props;
    const { isLoading, data, error, setLoading, setData, setError, execute } = useCase(readUserGroups, false);
    const valueUserGroupsState = useMemo(() => ({ isLoading, userGroups: data, error }), [isLoading, data, error]);
    const valueUserGroupsDispatch = useMemo(
        () => ({
            setLoading,
            setUserGroups: setData,
            setError,
            read: execute,
        }),
        [setLoading, setData, setError]
    );
    return (
        <UserGroupsStateContext.Provider value={valueUserGroupsState}>
            <UserGroupsDispatchContext.Provider value={valueUserGroupsDispatch}>{children}</UserGroupsDispatchContext.Provider>
        </UserGroupsStateContext.Provider>
    );
};
